// NOTE: Company descriptions (foundersNote) are duplicated in
// api/services/company-descriptions.json for the email newsletter.
// Keep both in sync when updating.

export interface Company {
  name: string;
  id: string;
  icon: string;
  url: string;
  description?: string;
  website?: string;
  remote?: string;
  employees?: string;
  countries?: string[];
  publishedJobs?: number;
  foundersNote?: string;
}

export const companies: Company[] = [
  {
    name: "Google",
    icon: "/google.svg",
    id: "google",
    url: "https://google.com",
    foundersNote:
      "Google has one of the largest EU engineering presences of any US company. Zurich, London, Munich, and Dublin all run core product work—Search, Maps, YouTube, Cloud. The Zurich office alone is one of their biggest globally. If you want FAANG-level work in Europe, this is one of the best options.",
  },
  {
    name: "Meta",
    icon: "/Meta_Logo-860x561.jpg",
    id: "meta",
    url: "https://metacareers.com",
    remote: "Yes",
    foundersNote:
      "Meta runs significant engineering out of London, Dublin, and Zurich. These teams work on core infrastructure, AI, and product—not support functions. They've been remote-friendly for EU roles, which widens the options beyond those three cities.",
  },
  {
    name: "Apple",
    icon: "/apple.svg",
    id: "apple",
    url: "https://apple.com",
    foundersNote:
      "Apple's EU engineering footprint is quieter than Google's or Meta's, but it's substantial. London, Munich, Cambridge, and Cork all have real teams. Cambridge in particular does chip and hardware work that's hard to find elsewhere in Europe.",
  },
  {
    name: "Amazon",
    icon: "/amazon.svg",
    id: "amazon",
    url: "https://amazon.com",
    foundersNote:
      "Amazon hires across a lot of European cities—Dublin, Berlin, London, Luxembourg, Gdańsk. The sheer volume of roles means there's usually something open, and they tend to move quickly on hiring. Engineering culture varies by team, which is worth keeping in mind.",
  },
  {
    name: "Microsoft",
    icon: "/microsoft.svg",
    id: "microsoft",
    url: "https://microsoft.com",
    foundersNote:
      "Microsoft has offices across most of Europe and tends to hire steadily. They're not as flashy as some of the other FAANG companies on this board, but the stability, benefits, and breadth of engineering work are hard to argue with.",
  },
  {
    name: "Netflix",
    id: "netflix",
    icon: "/nficon2016.png",
    employees: "13,000+ (3,000 engineers)",
    remote: "No",
    countries: ["us", "sg", "nl"],
    publishedJobs: 222,
    url: "https://netflix.com/",
    foundersNote:
      "Netflix has a small but well-paid EU presence—Amsterdam is their main European engineering hub. They're famously selective and don't hire in high volume, so when a role appears here, it's worth paying attention to.",
  },
  {
    name: "Reddit",
    id: "reddit",
    icon: "/android-icon-192x192.png",
    remote: "Yes",
    employees: "2,000+",
    countries: ["us", "ca", "ie", "nl", "de", "gb"], // TODO: Set in DB table after parsing
    publishedJobs: 222,
    url: "https://reddit.com/",
    foundersNote:
      "Reddit has been expanding into Europe with roles in Dublin, London, and Amsterdam. The engineering team is still relatively small compared to other companies on this board, which means more ownership per person.",
  },
  {
    name: "Spotify",
    id: "spotify",
    icon: "/1200px-Spotify_logo_without_text.svg.png",
    employees: "5,000+",
    remote: "No",
    countries: [],
    publishedJobs: 222,
    url: "https://spotify.com/",
    foundersNote:
      "Spotify is headquartered in Stockholm, which makes them one of the few companies on this board that's actually European. They have engineering teams across Stockholm, London, and other EU cities. Strong engineering culture and one of the best-known tech brands to come out of Europe.",
  },
  //{
  //name: "Twitter",
  //icon: "/twitter.ico",
  //},
  {
    name: "GitHub",
    id: "github",
    icon: "/github.svg",
    employees: "2,500+",
    remote: "Yes",
    countries: ["au", "es", "gb", "us", "de"],
    publishedJobs: 222,
    url: "https://github.com",
    foundersNote:
      "GitHub is fully remote-friendly and has engineers across multiple EU countries. As a developer tools company, the engineering work is directly relevant to what most of us do every day. They're part of Microsoft but operate with a lot of autonomy.",
  },
  {
    name: "HubSpot",
    id: "hubspot",
    icon: "/hubspot.png",
    remote: "Yes",
    employees: "7,500+",
    countries: ["us", "ie", "de", "es", "gb"],
    publishedJobs: 222,
    url: "https://hubspot.com",
    foundersNote:
      "HubSpot has a big European presence, especially in Dublin. They're consistently rated as one of the best places to work, and the engineering team has grown significantly. Good option if you want a product-focused role at a company that takes culture seriously.",
  },
  {
    name: "Atlassian",
    id: "atlassian",
    icon: "/30_Atlassian_logo_logos-1024.png",
    remote: "Yes",
    employees: "10,000+",
    countries: ["us", "au", "in"],
    publishedJobs: 222,
    url: "https://atlassian.com",
    foundersNote:
      "Atlassian is remote-first now, which makes them accessible from most EU countries. The products—Jira, Confluence, Bitbucket—are used by almost every engineering team, so the scale of impact is real even if the company isn't as visible as FAANG.",
  },
  {
    name: "Stripe",
    id: "stripe",
    icon: "/stripe.svg",
    remote: "Yes",
    employees: "7,000+",
    countries: ["us", "ie", "gb", "de"],
    publishedJobs: 222,
    url: "https://stripe.com",
    foundersNote:
      "Stripe has engineering teams in Dublin and London. They're known for hiring strong engineers and giving them hard problems—payments infrastructure at global scale. The bar is high, but the work is genuinely interesting.",
  },
  {
    name: "Mozilla",
    id: "mozilla",
    icon: "/mozilla.svg",
    remote: "Yes",
    employees: "750+",
    countries: ["us", "de", "ca", "fr", "gb"],
    publishedJobs: 222,
    url: "https://mozilla.com",
    foundersNote:
      "Mozilla is one of the few mission-driven companies on this board. They're fully remote and the team is small, so roles don't come up often. When they do, it's a chance to work on one of the most important open-source projects in the world.",
  },
  {
    name: "NVIDIA",
    id: "nvidia",
    icon: "/nvidia.svg",
    remote: "Yes",
    employees: "26,000+",
    url: "https://nvidia.com",
    foundersNote:
      "NVIDIA has become one of the most important companies in tech thanks to AI and GPU computing. They have EU roles across several countries. If you're interested in hardware, CUDA, or ML infrastructure, they're hard to beat.",
  },
  {
    name: "MongoDB",
    id: "mongo",
    icon: "/mongo.svg",
    url: "https://mongodb.com",
    remote: "Yes",
    employees: "",
    foundersNote:
      "MongoDB is remote-friendly and has EU roles in Dublin and other cities. Developer tools and databases are a space where the engineering problems are genuinely hard—scale, consistency, performance. Worth watching if you're into infrastructure.",
  },
  {
    name: "Datadog",
    id: "datadog",
    icon: "/datadog.svg",
    url: "https://www.datadoghq.com/",
    remote: "Yes",
    employees: "",
    foundersNote:
      "Datadog has grown fast and has engineering teams across Europe, including Paris where they were originally founded. Observability is a space where the technical challenges are real—high-throughput data, distributed systems, real-time processing.",
  },
  {
    name: "AWS",
    id: "aws",
    icon: "/aws.png",
    url: "https://aws.amazon.com/",
    remote: "No",
    employees: "",
    foundersNote:
      "AWS has engineering teams in Dublin, Berlin, and other EU cities. The scale is enormous—most of the internet runs on their infrastructure. Not remote-friendly, but the breadth of engineering work is unmatched.",
  },
  {
    name: "Personio",
    id: "personio",
    icon: "/personio.svg",
    url: "https://personio.com",
    foundersNote:
      "Personio is one of the bigger European-born companies on this board. Based in Munich, they're building HR software specifically for the European market. If you want to work at a scale-up that's actually European, not just a US company with an EU office, Personio is a solid pick.",
  },
  {
    name: "Uber",
    id: "uber",
    icon: "/uber.svg",
    url: "https://uber.com",
    foundersNote:
      "Uber has engineering teams in Amsterdam and other EU cities. The ride-hailing and delivery platform generates genuinely hard distributed systems and real-time problems. Good option if you want to work on systems at serious scale.",
  },
  {
    name: "eBay",
    id: "ebay",
    icon: "/ebay.svg",
    url: "https://ebay.com",
    foundersNote:
      "eBay has been around longer than most companies on this board, but they still run significant engineering across Europe. The marketplace problems—search, recommendations, payments, trust—are technically interesting at their scale.",
  },
  {
    name: "GitLab",
    id: "gitlab",
    icon: "/gitlab.svg",
    url: "https://gitlab.com",
    foundersNote:
      "GitLab is fully remote with no offices. They've been remote-first since the beginning, which makes them one of the most location-flexible companies on this board. The entire product is built in the open, which is unusual at this scale.",
  },
  {
    name: "Miro",
    id: "miro",
    icon: "/miro.svg",
    url: "https://miro.com",
    foundersNote:
      "Miro is another European success story—now headquartered in Amsterdam. The product is used by millions of teams, and they've been growing their EU engineering presence. Good option for product-focused engineering work.",
  },
  {
    name: "RevenueCat",
    id: "revenuecat",
    icon: "/revenuecat.png",
    url: "https://revenuecat.com",
    foundersNote:
      "RevenueCat is a smaller company, but they're remote-first and the engineering work is focused on subscription infrastructure that thousands of apps depend on. If you prefer a smaller team with outsized impact, they're worth a look.",
  },
  {
    name: "Databricks",
    id: "databricks",
    icon: "/databricks.svg",
    url: "https://databricks.com",
    foundersNote:
      "Databricks has been growing aggressively in Europe with roles across several EU cities. The data and AI space is moving fast, and they're at the centre of it. Strong engineering culture with academic roots.",
  },
  {
    name: "Booking.com",
    id: "booking",
    icon: "/booking.svg",
    url: "https://booking.com",
    foundersNote:
      "Booking.com is headquartered in Amsterdam and is one of the largest engineering organisations in Europe. The scale of experimentation they run is unusual—thousands of A/B tests at any given time. One of the few EU-headquartered companies operating at this scale.",
  },
  {
    name: "Delivery Hero",
    id: "deliveryhero",
    icon: "/deliveryhero.svg",
    url: "https://deliveryhero.com",
    foundersNote:
      "Delivery Hero is Berlin-based and operates across dozens of countries. The engineering challenges around real-time logistics, marketplace dynamics, and scale are genuinely hard. One of the bigger European tech companies that doesn't get as much attention as it should.",
  },
  {
    name: "Wayfair",
    id: "wayfair",
    icon: "/wayfair.svg",
    url: "https://wayfair.com",
    foundersNote:
      "Wayfair has engineering teams in Berlin and London. E-commerce at this scale involves interesting problems—search, personalisation, supply chain optimisation. Less well-known than Amazon but with a more focused product.",
  },
  {
    name: "Snowflake",
    id: "snowflake",
    icon: "/snowflake.svg",
    url: "https://snowflake.com",
    foundersNote:
      "Snowflake has been expanding their EU engineering presence. The data warehousing space is competitive and technically deep. If you're into distributed systems and data infrastructure, they're one of the more interesting companies to watch.",
  },
  {
    name: "Adobe",
    id: "adobe",
    icon: "/adobe.svg",
    url: "https://adobe.com",
    foundersNote:
      "Adobe has had EU engineering teams for a long time, including a significant presence in Romania. The creative tools are used by millions, and they're increasingly investing in AI-powered features. More technically interesting than people give them credit for.",
  },
  {
    name: "Monzo",
    id: "monzo",
    icon: "/monzo.svg",
    url: "https://monzo.com",
    foundersNote:
      "Monzo is one of the UK's most successful fintech companies. The engineering team works in Go and has built most of their banking infrastructure from scratch. If you're interested in fintech and want to work at a company that's genuinely product-led, Monzo is a standout.",
  },
  {
    name: "Deliveroo",
    id: "deliveroo",
    icon: "/deliveroo.svg",
    url: "https://deliveroo.com",
    foundersNote:
      "Deliveroo is London-based and one of Europe's biggest delivery platforms. The real-time logistics and marketplace engineering is technically challenging. They've also been investing heavily in their advertising and merchant tools.",
  },
  {
    name: "Phantom",
    id: "phantom",
    icon: "/phantom.svg",
    url: "https://phantom.app",
    foundersNote:
      "Phantom is a smaller company, but they're building one of the most-used crypto wallets. Fully remote and focused on making web3 accessible. If you're interested in crypto engineering without the hype, they're worth watching.",
  },
  {
    name: "Kraken",
    id: "kraken",
    icon: "/kraken.svg",
    url: "https://kraken.com",
    foundersNote:
      "Kraken is one of the oldest and most established crypto exchanges. They take security seriously and have a strong engineering reputation. Remote-friendly with EU roles. A more serious option in the crypto space.",
  },
  {
    name: "Elastic",
    id: "elastic",
    icon: "/elastic.svg",
    url: "https://elastic.co",
    foundersNote:
      "Elastic is the company behind Elasticsearch, which you've probably used or at least queried. They're fully remote and have a strong open-source culture. The engineering problems around search, observability, and security at scale are genuinely interesting.",
  },
  {
    name: "Palantir",
    id: "palantir",
    icon: "/palantir.svg",
    url: "https://palantir.com",
    foundersNote:
      "Palantir has EU offices in London and other cities. The work is different from most companies on this board—data platforms for government and enterprise clients. The engineering is technically deep, and they're selective about hiring.",
  },
  {
    name: "Squarespace",
    id: "squarespace",
    icon: "/squarespace.svg",
    url: "https://squarespace.com",
    foundersNote:
      "Squarespace has a smaller EU footprint, but they hire remotely for some roles. The product is well-designed and used by millions. If you care about design-driven engineering and clean user experiences, they're worth a look.",
  },
  {
    name: "CircleCI",
    id: "circleci",
    icon: "/circleci.svg",
    url: "https://circleci.com",
    foundersNote:
      "CircleCI is in the CI/CD space, which means the engineering work is directly relevant to developer workflows. Smaller team, which usually means more ownership per person.",
  },
  {
    name: "Tinder",
    id: "tinder",
    icon: "/tinder.svg",
    url: "https://tinder.com",
    foundersNote:
      "Tinder has engineering roles in some EU cities. The product is used by tens of millions of people, and the engineering challenges around matching, scale, and real-time communication are real. Part of Match Group.",
  },
  {
    name: "Quora",
    id: "quora",
    icon: "/quora.svg",
    url: "https://quora.com",
    foundersNote:
      "Quora is remote-friendly and has a smaller engineering team. The product has shifted toward AI with Poe. If you're interested in LLM-powered products and knowledge platforms, they're doing interesting work in that space.",
  },
  {
    name: "Yelp",
    id: "yelp",
    icon: "/yelp.svg",
    url: "https://yelp.com",
    foundersNote:
      "Yelp has some EU presence, though it's primarily US-focused. The local search and review platform generates interesting problems around NLP, search ranking, and trust. Not the biggest EU employer on this board, but worth watching if roles appear.",
  },
  {
    name: "Confluent",
    id: "confluent",
    icon: "/confluent.svg",
    url: "https://confluent.io",
    foundersNote:
      "Confluent is the company behind Apache Kafka. If you've worked with event streaming or real-time data, you probably know their stack. They have EU roles and the engineering work is technically deep—distributed systems at their core.",
  },
  {
    name: "Goldman Sachs",
    id: "goldman",
    icon: "/goldman.svg",
    url: "https://www.goldmansachs.com",
    foundersNote:
      "Goldman Sachs has large engineering teams in London and other EU cities. They've been investing heavily in technology, and the engineering work goes well beyond traditional finance—platform engineering, cloud infrastructure, developer tools. The pay is competitive with FAANG.",
  },
  {
    name: "Revolut",
    id: "revolut",
    icon: "/revolut.svg",
    url: "https://www.revolut.com",
    foundersNote:
      "Revolut is one of Europe's biggest fintech companies, headquartered in London. They move fast and hire aggressively across the EU. The engineering challenges around building a global financial platform are real. High pace, high ambition.",
  },
  {
    name: "Buffer",
    id: "buffer",
    icon: "/buffer.svg",
    url: "https://buffer.com",
    foundersNote:
      "Buffer is fully remote and has been since the beginning. They're transparent about everything—salaries, revenue, how they work. It's a smaller team, which means you'd have a lot of ownership. Good fit if you value work-life balance and transparency.",
  },
  {
    name: "Cloudflare",
    id: "cloudflare",
    icon: "/cloudflare.png",
    url: "https://cloudflare.com",
    foundersNote:
      "Cloudflare has EU offices in London, Lisbon, and Munich. They sit at the edge of the internet—literally. The engineering work around CDN, security, and performance is technically deep. One of the more interesting infrastructure companies to work at.",
  },
  {
    name: "Maze",
    id: "maze",
    icon: "/maze.svg",
    url: "https://maze.co",
    foundersNote:
      "Maze is a product research platform based in Europe. Smaller team, product-focused engineering, remote-friendly. If you're into design and UX tooling, they're one of the few companies in this space hiring in Europe.",
  },
  {
    name: "Adyen",
    id: "adyen",
    icon: "/adyen.svg",
    url: "https://adyen.com",
    foundersNote:
      "Adyen is headquartered in Amsterdam and is one of Europe's most successful fintech companies. They built their payments platform end-to-end, which is unusual—no middleware, no legacy. The engineering is technically interesting and the pay is competitive.",
  },
  {
    name: "Twilio",
    id: "twilio",
    icon: "/twilio.svg",
    url: "https://twilio.com",
    foundersNote:
      "Twilio has EU roles and the engineering work is focused on communications infrastructure—APIs for voice, messaging, video. The core product is widely used and the engineering problems are interesting.",
  },
  {
    name: "Celonis",
    id: "celonis",
    icon: "/celonis.svg",
    url: "https://celonis.com",
    foundersNote:
      "Celonis is a Munich-based company doing process mining. They're one of the bigger European tech companies that most people outside the space haven't heard of. If you're interested in data-heavy enterprise software with European roots, they're worth a look.",
  },
  {
    name: "Figma",
    id: "figma",
    icon: "/figma.svg",
    url: "https://figma.com",
    foundersNote:
      "Figma changed how designers work, and they've been expanding into Europe. The engineering work is focused on real-time collaboration, which is a hard problem. If you care about developer and design tools, they're one of the most exciting companies on this board.",
  },
  {
    name: "Pinterest",
    id: "pinterest",
    icon: "/pinterest.svg",
    url: "https://pinterest.com",
    foundersNote:
      "Pinterest has some EU engineering presence. The product is visual search and discovery at scale—the engineering problems around recommendations, image understanding, and search are technically interesting.",
  },
  {
    name: "Epic Games",
    id: "epicgames",
    icon: "/epicgames.svg",
    url: "https://epicgames.com",
    foundersNote:
      "Epic Games makes Unreal Engine and Fortnite. They have EU offices and the engineering work spans game development, 3D graphics, and platform infrastructure. If you're interested in game tech or real-time 3D, they're one of the few companies doing it at this scale.",
  },
  {
    name: "Sentry",
    id: "sentry",
    icon: "/sentry.svg",
    url: "https://sentry.io",
    foundersNote:
      "Sentry is an error monitoring platform used by a lot of engineering teams. Open-source at their core and with EU roles available. Developer tools company, which means the engineering work is directly relevant to what most of us deal with every day.",
  },
  {
    name: "Grafana Labs",
    id: "grafanalabs",
    icon: "/grafanalabs.svg",
    url: "https://grafana.com",
    remote: "Yes",
    foundersNote:
      "Grafana Labs is fully remote and the company behind Grafana, Loki, and Mimir. If you've used their open-source tools, you know how good they are. The engineering work is focused on observability infrastructure, and they're one of the most developer-loved companies in the space.",
  },
  {
    name: "The Trade Desk",
    id: "thetradedesk",
    icon: "/thetradedesk.svg",
    url: "https://thetradedesk.com",
    foundersNote:
      "The Trade Desk has EU offices and works on programmatic advertising infrastructure. It's not the most glamorous space, but the engineering challenges around real-time bidding, data processing at scale, and ML are technically deep.",
  },
  {
    name: "Grammarly",
    id: "grammarly",
    icon: "/grammarly.svg",
    url: "https://grammarly.com",
    foundersNote:
      "Grammarly has been expanding into Europe. The product is AI-powered writing assistance, which means the engineering work touches NLP, ML, and real-time text processing. If you're interested in applied AI at consumer scale, they're worth following.",
  },
  {
    name: "Contentful",
    id: "contentful",
    icon: "/contentful.svg",
    url: "https://contentful.com",
    foundersNote:
      "Contentful is a Berlin-based headless CMS company. If you've worked in web development, you've probably come across their product. European-born, and the engineering work is focused on content infrastructure and APIs.",
  },
  {
    name: "Plaid",
    id: "plaid",
    icon: "/plaid.svg",
    url: "https://plaid.com",
    foundersNote:
      "Plaid connects apps to bank accounts—they're the infrastructure layer for a lot of fintech products. They have some EU presence and the engineering work around financial data, security, and API design is technically interesting.",
  },
  {
    name: "Bumble",
    id: "bumble",
    icon: "/bumble.svg",
    url: "https://bumble.com",
    foundersNote:
      "Bumble has engineering teams in London and other EU cities. The product is used by tens of millions of people, and the engineering challenges are real—matching, real-time communication, trust and safety at scale.",
  },
  {
    name: "Optiver",
    id: "optiver",
    icon: "/optiver.svg",
    url: "https://optiver.com",
    foundersNote:
      "Optiver is a global market maker headquartered in Amsterdam. They're one of the most technically sophisticated trading firms in the world, with deep expertise in low-latency systems, quantitative research, and infrastructure engineering. Their Amsterdam office is the HQ and a major engineering hub.",
  },
  {
    name: "Wise",
    id: "wise",
    icon: "/wise.svg",
    url: "https://wise.com",
    foundersNote:
      "Wise (formerly TransferWise) is a London-based fintech that built international money transfers from scratch. They have major engineering hubs in Tallinn, London, and Budapest. The engineering challenges around real-time payments, compliance, and multi-currency infrastructure are technically deep.",
  },
  {
    name: "Intercom",
    id: "intercom",
    icon: "/intercom.svg",
    url: "https://intercom.com",
    foundersNote:
      "Intercom is a customer messaging platform with a major engineering hub in Dublin. They build real-time communication tools used by thousands of businesses. Strong product engineering culture with interesting problems around messaging infrastructure and AI.",
  },
  {
    name: "JetBrains",
    id: "jetbrains",
    icon: "/jetbrains.svg",
    url: "https://jetbrains.com",
    foundersNote:
      "JetBrains makes the IDEs that most developers love—IntelliJ, WebStorm, PyCharm, and Kotlin. Headquartered in Prague with offices across Europe. If you want to build developer tools used by millions of engineers, there are few better places.",
  },
  {
    name: "N26",
    id: "n26",
    icon: "/n26.svg",
    url: "https://n26.com",
    foundersNote:
      "N26 is a Berlin-based mobile bank that's one of Europe's most well-known fintech companies. The engineering team builds banking infrastructure from scratch—payments, compliance, fraud detection. A solid pick if you're interested in European fintech.",
  },
  {
    name: "Wolt",
    id: "wolt",
    icon: "/wolt.svg",
    url: "https://wolt.com",
    foundersNote:
      "Wolt is a Helsinki-born delivery platform now part of DoorDash. They have engineering teams across Northern and Central Europe. Known for strong engineering culture and interesting logistics and marketplace problems.",
  },
  {
    name: "Proton",
    id: "proton",
    icon: "/proton.svg",
    url: "https://proton.me",
    foundersNote:
      "Proton is the company behind ProtonMail and ProtonVPN, headquartered in Geneva. They're building privacy-focused tools used by millions. If you care about security, encryption, and digital privacy, Proton is one of the most mission-driven companies in Europe.",
  },
  {
    name: "GetYourGuide",
    id: "getyourguide",
    icon: "/getyourguide.svg",
    url: "https://getyourguide.com",
    foundersNote:
      "GetYourGuide is a Berlin-based travel experiences marketplace. They connect travelers with tours and activities worldwide. The engineering work spans marketplace dynamics, search, personalization, and mobile at scale.",
  },
  {
    name: "Trade Republic",
    id: "traderepublic",
    icon: "/traderepublic.svg",
    url: "https://traderepublic.com",
    foundersNote:
      "Trade Republic is a Berlin-based neobroker and one of Europe's fastest-growing fintech companies. They're building investment infrastructure for millions of Europeans. The engineering challenges around real-time trading, regulatory compliance, and financial data are technically deep.",
  },
  {
    name: "Mollie",
    id: "mollie",
    icon: "/mollie.svg",
    url: "https://mollie.com",
    foundersNote:
      "Mollie is an Amsterdam-based payments company serving European businesses. They've grown rapidly and the engineering team works on payment processing, checkout optimization, and financial infrastructure. One of the Netherlands' most successful tech companies.",
  },
  {
    name: "Thought Machine",
    id: "thoughtmachine",
    icon: "/thoughtmachine.svg",
    url: "https://thoughtmachine.net",
    foundersNote:
      "Thought Machine is a London-based fintech building cloud-native core banking infrastructure. Their platform Vault powers banks around the world. The engineering is technically deep—distributed systems, financial transaction processing, and cloud infrastructure at scale.",
  },
  {
    name: "Mistral AI",
    id: "mistralai",
    icon: "/mistralai.svg",
    url: "https://mistral.ai",
    foundersNote:
      "Mistral AI is a Paris-based AI company building frontier large language models. Founded by former DeepMind and Meta researchers, they're one of Europe's most important AI companies. If you want to work on cutting-edge AI research and infrastructure in Europe, Mistral is the one to watch.",
  },
  {
    name: "SonarSource",
    id: "sonarsource",
    icon: "/sonarsource.svg",
    url: "https://sonarsource.com",
    foundersNote:
      "SonarSource is a Geneva-based company behind SonarQube and SonarCloud—code quality tools used by millions of developers. The engineering work focuses on static analysis, code security, and developer tooling. A strong option if you're into developer tools and code quality.",
  },
  {
    name: "n8n",
    id: "n8n",
    icon: "/n8n.svg",
    url: "https://n8n.io",
    foundersNote:
      "n8n is the leading open-source workflow automation platform. Based in Berlin with remote roles across Europe, backed by Sequoia with $240M raised. Salary data is limited, but equity upside at this stage could be significant. Engineering spans AI agents, cloud infrastructure, and core workflow engine development.",
  },
  {
    name: "Pleo",
    id: "pleo",
    icon: "/pleo.svg",
    url: "https://www.pleo.io",
    foundersNote:
      "Pleo is the European fintech building smart business spending solutions. Headquartered in Copenhagen with offices in London, Berlin, Stockholm, Madrid, and Lisbon. 800+ employees across 74+ nationalities. Engineering covers payments, cards, and expense automation.",
  },
  {
    name: "Anthropic",
    id: "anthropic",
    icon: "/anthropic.svg",
    url: "https://anthropic.com",
    foundersNote:
      "Anthropic is an AI safety company building Claude, one of the most capable large language models. They have engineering teams in London, Dublin, and Zurich working on core model development, infrastructure, and safety research. One of the highest-paying companies in AI.",
  },
  {
    name: "OpenAI",
    id: "openai",
    icon: "/openai.svg",
    url: "https://openai.com",
    foundersNote:
      "OpenAI builds ChatGPT and the models behind it. Its European engineering is concentrated in London, Dublin and Munich, working on the same ChatGPT and platform infrastructure as the San Francisco teams rather than on a regional sideline. Pay for the London roles is published on the posting and is among the highest on this board.",
  },
  {
    name: "Vercel",
    id: "vercel",
    icon: "/vercel.svg",
    url: "https://vercel.com",
    foundersNote:
      "Vercel is the company behind Next.js and a leading frontend deployment platform. They have engineering presence in Berlin and London. If you care about developer experience, frontend infrastructure, and edge computing, Vercel is one of the most interesting companies in the space.",
  },
  {
    name: "Okta",
    id: "okta",
    icon: "/okta.svg",
    url: "https://okta.com",
    foundersNote:
      "Okta is the leading identity and access management platform. They have a major engineering hub in Dublin and presence in London. The work involves security, authentication infrastructure, and identity protocols at enterprise scale.",
  },
  {
    name: "Klaviyo",
    id: "klaviyo",
    icon: "/klaviyo.svg",
    url: "https://klaviyo.com",
    foundersNote:
      "Klaviyo is a marketing automation platform focused on e-commerce. They have engineering teams in London and Dublin with strong compensation. The engineering challenges involve large-scale data processing, ML-powered personalization, and real-time messaging infrastructure.",
  },
  {
    name: "Duolingo",
    id: "duolingo",
    icon: "/duolingo.svg",
    url: "https://duolingo.com",
    foundersNote:
      "Duolingo is the world's most popular language learning app with hundreds of millions of users. Their Berlin office runs core engineering work. The product combines AI, gamification, and education—technically interesting problems at massive consumer scale.",
  },
  {
    name: "Linear",
    id: "linear",
    icon: "/linear.svg",
    url: "https://linear.app",
    foundersNote:
      "Linear is a modern project management tool beloved by engineering teams. They're remote-first with a Berlin coworking hub and hire across European timezones. The product is known for its exceptional performance and polish—the engineering reflects that standard.",
  },
  {
    name: "Bolt",
    id: "bolteu",
    icon: "/bolteu.svg",
    url: "https://bolt.eu",
    foundersNote:
      "Bolt is a Tallinn-based mobility platform operating across 45+ countries in Europe and Africa. Their engineering team in Estonia works on ride-hailing, food delivery, and micro-mobility at serious scale. One of Europe's most valuable tech companies with a $6.8B valuation.",
  },
  {
    name: "Qualio",
    id: "qualio",
    icon: "/qualio.svg",
    url: "https://qualio.com",
    remote: "Yes",
    foundersNote:
      "Qualio is a Dublin-based quality management platform for life sciences companies. Fully remote across the EU with strong compensation—confirmed at €106–150K for senior engineers in Ireland. If you want B2B SaaS with real regulatory complexity and a remote-first culture, this is a solid pick.",
  },
  {
    name: "GSA Capital",
    id: "gsacapital",
    icon: "/gsacapital.png",
    url: "https://gsacapital.com",
    foundersNote:
      "GSA Capital is a London-based quantitative trading firm. Small, technical team focused on systematic strategies. Senior software engineers earn £101–184K. They don't post many roles but when they do, they're looking for strong engineers who can work close to the trading systems.",
  },
  {
    name: "Five Rings",
    id: "fiverings",
    icon: "/fiverings.png",
    url: "https://fiverings.com",
    foundersNote:
      "Five Rings is a proprietary trading firm with offices in London and Amsterdam. They hire software developers and systems engineers who work on low-latency trading infrastructure in C++ and Python. Compensation is at the top end of the market—£137–198K TC for London roles.",
  },
  {
    name: "Old Mission Capital",
    id: "oldmission",
    icon: "/oldmission.svg",
    url: "https://oldmissioncapital.com",
    foundersNote:
      "Old Mission Capital is a global market-making firm with a London engineering office. Their C++ and Python engineers work on options and ETF market-making systems. One of the higher-paying firms in the space—$230K+ total comp for experienced engineers.",
  },
  {
    name: "Tower Research Capital",
    id: "towerresearch",
    icon: "/towerresearch.svg",
    url: "https://tower-research.com",
    foundersNote:
      "Tower Research Capital is a high-frequency trading firm with engineering offices in London and Amsterdam. They hire software engineers and quantitative developers for low-latency market infrastructure. London roles pay £117–188K. One of the largest HFT firms globally.",
  },
  {
    name: "UiPath",
    id: "uipath",
    icon: "/uipath.png",
    url: "https://uipath.com",
    foundersNote:
      "UiPath is a Romanian-born automation platform that went public in 2021. Their Bucharest engineering office is one of the largest in Romania, and compensation is confirmed at €100–130K for senior engineers—exceptional for the market. If you want scale-up engineering challenges with a strong local team, UiPath Bucharest is worth looking at.",
  },
  {
    name: "Squarepoint Capital",
    id: "squarepoint",
    icon: "/squarepoint.svg",
    url: "https://www.squarepointcap.com",
    foundersNote:
      "Squarepoint Capital is a global quantitative hedge fund with engineering offices in London, Warsaw, Madrid, and Geneva. They hire software developers for data infrastructure, quant platforms, and trading systems in Python, C++, and KDB+. Compensation is at the high end for European quant firms—£82–150K TC in London, with Warsaw and Madrid roles also well above market.",
  },
  {
    name: "Maven Securities",
    id: "mavensecurities",
    icon: "/mavensecurities.png",
    url: "https://www.mavensecurities.com",
    foundersNote:
      "Maven Securities is a London-based options market-making firm with an Amsterdam office. They hire C++, Python, and platform engineers for low-latency trading systems. Senior engineers earn £99–180K. Small team, high impact—if you want to work close to the trading systems at a prop shop with genuine EU presence, Maven is worth a look.",
  },
  {
    name: "QRT",
    id: "qrt",
    icon: "/qrt.svg",
    url: "https://www.qube-rt.com",
    foundersNote:
      "Qube Research & Technologies (QRT) is a global quantitative hedge fund with major engineering hubs in London, Paris, Amsterdam, and Zurich. One of the most active EU tech hirers in the quant space—they post hundreds of roles across software engineering, data engineering, FPGA, infrastructure, and security. Senior engineers earn £91–134K in London and comparable in Paris/Amsterdam.",
  },
  {
    name: "Chainalysis",
    id: "chainalysis",
    icon: "/chainalysis.svg",
    url: "https://www.chainalysis.com",
    foundersNote:
      "Chainalysis is the leading blockchain analytics company, with a real engineering office in Aarhus, Denmark (plus London). They build blockchain data infrastructure, investigations tooling, and compliance software used by governments and financial institutions globally. Senior engineers in Europe earn $140–200K.",
  },
  {
    name: "Form3",
    id: "form3",
    icon: "/form3.svg",
    url: "https://form3.tech",
    foundersNote:
      "Form3 is a London-based cloud-native payments platform processing billions in transactions for European banks. Small, high-trust engineering team working on Go-based microservices, Kubernetes infrastructure, and real-time payment processing. Senior engineers earn £85–100K—solid for a payments infrastructure specialist.",
  },
  {
    name: "Autodesk",
    id: "autodesk",
    icon: "/autodesk.svg",
    url: "https://www.autodesk.com/careers",
    foundersNote:
      "Autodesk makes the software that architects, engineers, and filmmakers use to design and create. Their EU engineering hubs in Dublin, Barcelona, and Prague work on AutoCAD, Fusion, and cloud platform infrastructure. Solid pay, stable business, and meaningful scale.",
  },
  {
    name: "Helsing",
    id: "helsing",
    icon: "/helsing.svg",
    url: "https://helsing.ai",
    foundersNote:
      "Helsing is Europe's best-funded defence-AI company, building AI software, strike drones and electronic-warfare systems out of Munich, with engineering teams in Berlin, London, Paris and Barcelona. They hire AI research engineers, backend, frontend and embedded software engineers, and electronics and systems engineers. Senior engineers earn a median of around €152K total comp in Munich (€176K at L3) and around £122K in London.",
  },
  {
    name: "Wayve",
    id: "wayve",
    icon: "/wayve.svg",
    url: "https://wayve.ai",
    foundersNote:
      "Wayve is a London-based autonomous-driving company training end-to-end AI driving models, with a growing engineering presence in Germany. The London team hires ML engineers, robotics and embedded software engineers, SREs and simulation and validation engineers. Senior engineers in London earn a median of around £130K total comp, rising to £174K at TC4.",
  },
  {
    name: "Anduril",
    id: "anduril",
    icon: "/anduril.svg",
    url: "https://www.anduril.com",
    foundersNote:
      "Anduril builds autonomous defence systems: drones, sensors and the Lattice command-and-control software that ties them together. Their UK engineering team in London (with flight testing in Llanbedr, Wales) hires mission software, robotics, product security and DevOps engineers. London engineers earn a median of around £146K total comp.",
  },
  {
    name: "Waymo",
    id: "waymo",
    icon: "/waymo.svg",
    url: "https://waymo.com",
    foundersNote:
      "Waymo runs the world's largest fully driverless ride-hailing service, and their London office hosts machine learning, simulation and AI-foundations engineering teams. We deliberately leave out their Warsaw roles, which pay well below the London bar. London pay is exceptional: around £109K total comp at L3 and £227K at L5.",
  },
  {
    name: "Isomorphic Labs",
    id: "isomorphiclabs",
    icon: "/isomorphiclabs.png",
    url: "https://www.isomorphiclabs.com",
    foundersNote:
      "Isomorphic Labs is Alphabet's AI drug-discovery company, spun out of DeepMind and headquartered in London with a second office in Lausanne. They hire research scientists, ML research engineers and software and data platform engineers to build on AlphaFold-class models. Senior engineers in London earn a median of around £238K total comp, among the highest in Europe.",
  },
  {
    name: "Scale AI",
    id: "scaleai",
    icon: "/scaleai.svg",
    url: "https://scale.com",
    foundersNote:
      "Scale AI provides the data and evaluation infrastructure behind frontier AI models, plus generative-AI applications for enterprises and governments. Their London office hires platform, infrastructure, ML and forward-deployed engineers. Senior engineers in London earn a median of around £161K total comp (£218K at L4).",
  },
  {
    name: "Poolside",
    id: "poolside",
    icon: "/poolside.svg",
    url: "https://poolside.ai",
    foundersNote:
      "Poolside is building foundation models for software engineering, with a remote-first team spread across EMEA and a London hub. Roles cover inference infrastructure, agent sandboxes, evaluations, and pre-/post-training research.",
  },
  {
    name: "Synthesia",
    id: "synthesia",
    icon: "/synthesia.png",
    url: "https://www.synthesia.io",
    foundersNote:
      "Synthesia is the London-based AI video platform (avatars, dubbing, generative video) and one of the UK's biggest AI scale-ups. Engineering and research sit in London, with several roles open to anyone in Europe or remote in the UK.",
  },
  {
    name: "Modal",
    id: "modal",
    icon: "/modal.svg",
    url: "https://modal.com",
    foundersNote:
      "Modal is a serverless cloud platform for running AI and data workloads, built by a small team of systems engineers. Alongside New York and San Francisco they have an engineering office in Stockholm and hire in London. Stockholm Member of Technical Staff roles are posted at $140–200K plus equity.",
  },
  {
    name: "Workato",
    id: "workato",
    icon: "/workato.png",
    url: "https://www.workato.com",
    foundersNote:
      "Workato is an enterprise automation and integration platform (iPaaS) used by thousands of companies. Their European engineering teams sit in Barcelona, Berlin, Amsterdam, and London, working on the core platform in Rust and Go.",
  },
  {
    name: "Neo4j",
    id: "neo4j",
    icon: "/neo4j.svg",
    url: "https://neo4j.com",
    foundersNote:
      "Neo4j builds the leading graph database, with core engineering split between London and Malmö. Teams work on the database kernel, clustering and sharding, the Aura cloud platform, and graph data science.",
  },
  {
    name: "GoCardless",
    id: "gocardless",
    icon: "/gocardless.svg",
    url: "https://gocardless.com",
    foundersNote:
      "GoCardless is a London fintech that processes bank-to-bank payments (direct debit and open banking) for tens of thousands of businesses. Engineering is headquartered in London, with additional hubs in Lisbon and Riga; we only list the London roles.",
  },
  {
    name: "Riot Games",
    id: "riotgames",
    icon: "/riotgames.svg",
    url: "https://www.riotgames.com",
    foundersNote:
      "Riot Games is the studio behind League of Legends and VALORANT. Their Dublin office is Riot's EMEA hub and hosts engineering teams for game, platform, and publishing technology. We list Dublin roles only.",
  },
  {
    name: "Nebius",
    id: "nebius",
    icon: "/nebius.svg",
    url: "https://nebius.com",
    foundersNote:
      "Nebius is the Amsterdam-headquartered, Nasdaq-listed AI cloud that spun out of Yandex. They build GPU clusters, their own server hardware, and a full cloud stack for AI training and inference, with engineering concentrated in Amsterdam and teams in London, Prague, Berlin, Finland, and Paris plus remote roles across Europe.",
  },
  {
    name: "Pure Storage",
    id: "purestorage",
    icon: "/purestorage.svg",
    url: "https://www.purestorage.com",
    foundersNote:
      "Pure Storage builds all-flash enterprise storage arrays and the Portworx Kubernetes data platform. Their Prague office is the company's main engineering hub outside the US, working on file systems, cloud services, and AI tooling in C++, Go, and Java. Senior engineers in Prague earn a median of CZK 3.14M (about €126K), with MTS4 at CZK 3.31M—exceptional for the Czech market.",
  },
  {
    name: "Tripadvisor",
    id: "tripadvisor",
    icon: "/tripadvisor.svg",
    url: "https://www.tripadvisor.com",
    foundersNote:
      "Tripadvisor is the world's largest travel guidance platform, and its Viator and Bókun brands run on engineering teams in London and Oxford. They work on search, ranking, machine learning, and mobile apps at consumer scale.",
  },
  {
    name: "Affirm",
    id: "affirm",
    icon: "/affirm.png",
    url: "https://www.affirm.com",
    foundersNote:
      "Affirm is the US buy-now-pay-later fintech, and its international engineering team works fully remote from Spain and the UK on payments, identity, and consumer products. Senior backend roles are posted at €86–122K in Spain and £113–161K in the UK.",
  },
  {
    name: "Toast",
    id: "toast",
    icon: "/toast.svg",
    url: "https://pos.toasttab.com",
    foundersNote:
      "Toast is the restaurant point-of-sale and payments platform, and Dublin is its main engineering hub outside the US. Teams there build payments, AI foundations, the data platform, and core infrastructure.",
  },
  {
    name: "Asana",
    id: "asana",
    icon: "/asana.svg",
    url: "https://asana.com",
    foundersNote:
      "Asana builds the work-management platform teams use to plan and track their projects. Their Warsaw office hosts foundations, AI, security, and data engineering, with a smaller presence in Dublin. Posted senior base salaries run PLN 31.9–40.6K per month—top of the Polish market.",
  },
  {
    name: "Ripple",
    id: "ripple",
    icon: "/ripple.svg",
    url: "https://ripple.com",
    foundersNote:
      "Ripple builds enterprise blockchain and crypto payment infrastructure, including RippleNet, the XRP Ledger, and its stablecoin products. Their European engineering teams sit in London, Dublin, and Geneva, working on payments connectivity, security, data, and infrastructure.",
  },
  {
    name: "Jane Street",
    id: "janestreet",
    icon: "/janestreet.svg",
    url: "https://www.janestreet.com",
    foundersNote:
      "Jane Street is a quantitative trading firm famous for running its entire stack in OCaml. The London office hires software engineers, FPGA/ASIC hardware engineers, Linux and network engineers, cybersecurity engineers, and ML researchers.",
  },
  {
    name: "DRW",
    id: "drw",
    icon: "/drw.svg",
    url: "https://drw.com",
    foundersNote:
      "DRW is a Chicago-born proprietary trading firm with a large London engineering office and a smaller one in Amsterdam. Engineers work on C++ and Python trading platforms, FPGA development, cloud and Linux infrastructure, and crypto systems for its Cumberland arm.",
  },
  {
    name: "Hudson River Trading",
    id: "hrt",
    icon: "/hrt.svg",
    url: "https://www.hudsonrivertrading.com",
    foundersNote:
      "Hudson River Trading is an algorithmic trading firm whose London office builds low-latency C++ trading systems, HPC and GPU infrastructure, hardware design, and AI research tooling, with a smaller trading-support team in Dublin. Most roles are posted for London alongside the US offices. Postings list $200–300K base.",
  },
  {
    name: "Jump Trading",
    id: "jumptrading",
    icon: "/jumptrading.png",
    url: "https://www.jumptrading.com",
    foundersNote:
      "Jump Trading is a Chicago-based quantitative trading firm with engineering and research teams in London, Amsterdam, and Paris. They hire HPC and systems engineers, SREs, FPGA engineers, ML research engineers, and quantitative researchers. An Amsterdam SRE posting lists €150–175K.",
  },
  {
    name: "IMC",
    id: "imc",
    icon: "/imc.svg",
    url: "https://www.imc.com",
    foundersNote:
      "IMC is a Dutch market maker headquartered in Amsterdam, with a digital-assets engineering team in Zug. They hire C++, Java, and Python software engineers, performance engineers, HPC systems architects, ML engineers, and quant developers.",
  },
  {
    name: "Flow Traders",
    id: "flowtraders",
    icon: "/flowtraders.svg",
    url: "https://www.flowtraders.com",
    foundersNote:
      "Flow Traders is an Amsterdam-headquartered ETF market maker listed on Euronext, with engineering teams in Amsterdam and London. They hire senior C++ software engineers, staff engineers for trading automation and backend platforms, research engineers, and engineering managers.",
  },
  {
    name: "Point72",
    id: "point72",
    icon: "/point72.png",
    url: "https://www.point72.com",
    foundersNote:
      "Point72 is Steve Cohen's hedge fund, and its Warsaw office is a real engineering hub building trading, treasury, compliance, and market-intelligence data platforms, with quant developers and researchers in London. Roles cover software engineering, data and platform engineering, and quantitative development for its Cubist systematic arm.",
  },
  {
    name: "Man Group",
    id: "mangroup",
    icon: "/mangroup.svg",
    url: "https://www.man.com",
    foundersNote:
      "Man Group is the world's largest publicly listed hedge fund, headquartered in London, where its AHL and Systematic teams run quantitative strategies on an open-source-friendly Python stack (they built ArcticDB). London roles include quantitative developers, Python and systems engineers, and quant researchers.",
  },
  {
    name: "Virtu Financial",
    id: "virtu",
    icon: "/virtu.svg",
    url: "https://www.virtu.com",
    foundersNote:
      "Virtu Financial is a NASDAQ-listed market maker whose European engineering is centred in Dublin, with a smaller team in London. Dublin hires full-stack developers, FPGA hardware engineers, and quantitative strategists, and London hires Java software engineers.",
  },
  {
    name: "Wintermute",
    id: "wintermute",
    icon: "/wintermute.svg",
    url: "https://www.wintermute.com",
    foundersNote:
      "Wintermute is one of the largest algorithmic trading firms in crypto, providing liquidity across most digital-asset exchanges and OTC. Engineering is in London: C++ and Python developers building low-latency trading systems, plus ML and quant researchers.",
  },
  {
    name: "XTX Markets",
    id: "xtxmarkets",
    icon: "/xtxmarkets.svg",
    url: "https://www.xtxmarkets.com",
    foundersNote:
      "XTX Markets is a London-based algorithmic trading firm and one of the largest liquidity providers in global markets, running on a massive in-house GPU research cluster. Engineering is in London: C++ software engineers, ML performance engineers, and security and shared-platform engineers.",
  },
  {
    name: "Black Forest Labs",
    id: "blackforestlabs",
    icon: "/blackforestlabs.svg",
    url: "https://blackforestlabs.ai",
    foundersNote:
      "Black Forest Labs is the Freiburg-based team behind the FLUX image and video generation models. Research and engineering are in Freiburg, with roles also open in Berlin, Munich, London, Amsterdam, and Paris. Posted EU base salary bands are €130–340K for research and member-of-technical-staff roles and €140–190K for product and developer-experience engineers.",
  },
  {
    name: "G-Research",
    id: "gresearch",
    icon: "/gresearch.svg",
    url: "https://www.gresearch.com",
    foundersNote:
      "G-Research is a London quantitative research and technology firm that runs one of the largest private compute platforms in Europe. Nearly all engineering is in London: software, infrastructure, Kubernetes, networking, security, and ML engineers, plus quantitative and ML researchers.",
  },
  {
    name: "ClickHouse",
    id: "clickhouse",
    icon: "/clickhouse.svg",
    url: "https://clickhouse.com",
    foundersNote:
      "ClickHouse builds the open-source columnar database of the same name and runs it as a managed cloud service. Engineering is remote-first across EMEA, with the core C++ database, cloud infrastructure and security teams hiring in the Netherlands, Germany, the UK and Spain.",
  },
  {
    name: "ElevenLabs",
    id: "elevenlabs",
    icon: "/elevenlabs.svg",
    url: "https://elevenlabs.io",
    foundersNote:
      "ElevenLabs builds AI voice and audio models—text-to-speech, dubbing and conversational agents—and is one of Europe's fastest-growing AI companies. Engineering and research teams are spread across London, Warsaw, Sofia and Berlin, plus remote roles across Europe. London engineers earn a median of £190K (packages start around £100K), and Poland-based research engineers start around PLN 950K.",
  },
  {
    name: "Lovable",
    id: "lovable",
    icon: "/lovable.svg",
    url: "https://lovable.dev",
    foundersNote:
      "Lovable is the Stockholm-based AI app builder that turns a prompt into a working full-stack application, and one of the fastest-growing startups Europe has produced. Engineering sits in Stockholm with a growing London office, working on the agent runtime, platform infrastructure and security. Senior engineers in Stockholm earn around SEK 1.68M (roughly €151K), with a median of SEK 1.37M.",
  },
  {
    name: "Cohere",
    id: "cohere",
    icon: "/cohere.svg",
    url: "https://cohere.com",
    foundersNote:
      "Cohere builds enterprise-focused large language models (Command, Embed, Rerank) and the North agent platform, with a strong focus on private and sovereign deployments. The European engineering and research teams are in London and Paris, with further roles in Berlin, Munich and Edinburgh and many postings open across Europe. London engineers earn a median of £176K, with L3 packages around £236K.",
  },
  {
    name: "Attio",
    id: "attio",
    icon: "/attio.svg",
    url: "https://attio.com",
    foundersNote:
      "Attio is a London-based, AI-native CRM built for modern go-to-market teams. The engineering team works on the product, platform and security from London and remotely across Poland, Portugal, Ireland and Germany. Senior engineers are posted at £95–125K in London and €95–125K for remote EU.",
  },
  {
    name: "Sierra",
    id: "sierra",
    icon: "/sierra.svg",
    url: "https://sierra.ai",
    foundersNote:
      "Sierra builds conversational AI agents for customer service at large consumer brands, co-founded by former Salesforce co-CEO Bret Taylor. Its European engineering teams sit in London, Paris, Madrid and Munich, building and deploying agents for enterprise customers. Posted ranges are exceptional: London software engineers at £150–315K, Paris €125–265K and Madrid €170–245K.",
  },
  {
    name: "Susquehanna (SIG)",
    id: "sig",
    icon: "/sig.svg",
    url: "https://www.sig.com",
    foundersNote:
      "Susquehanna International Group (SIG) is a global quantitative trading firm founded in Philadelphia and one of the largest options market makers. Its European technology hub is in Dublin, where software, trading-systems, site-reliability and data engineers build and run the firm's trading platform.",
  },
  {
    name: "Bloomberg",
    id: "bloomberg",
    icon: "/bloomberg.png",
    url: "https://bloomberg.com",
    foundersNote:
      "Bloomberg is the financial data, analytics and media company behind the Bloomberg Terminal. Its European engineering centres are in London and Dublin, where teams build the Terminal's data platforms, distributed databases, Kubernetes infrastructure and news systems.",
  },
  {
    name: "Checkout.com",
    id: "checkout",
    icon: "/checkout.png",
    url: "https://www.checkout.com",
    foundersNote:
      "Checkout.com is a London-headquartered global payments company that processes card, wallet and local payments for large online merchants. Its London engineering hub builds the acquiring, fraud-detection and payment-orchestration platform, with teams working in Go, .NET and cloud infrastructure.",
  },
  {
    name: "Applied Intuition",
    id: "appliedintuition",
    icon: "/appliedintuition.svg",
    url: "https://www.appliedintuition.com",
    foundersNote:
      "Applied Intuition builds the simulation, validation and vehicle software stack that car makers, truck OEMs and defence programmes use to develop autonomous and software-defined vehicles. Its European engineering teams in Munich, Stuttgart and London work on cloud infrastructure, the vehicle OS, perception and machine learning software.",
  },
  {
    name: "Zego",
    id: "zego",
    icon: "/zego.png",
    url: "https://www.zego.com",
    foundersNote:
      "Zego is a London-based insurtech that sells usage-based commercial motor insurance to delivery drivers, couriers and fleets, priced with telematics data. Its London data and engineering team builds the pricing, claims and telematics platform.",
  },
  {
    name: "Ashby",
    id: "ashby",
    icon: "/ashby.png",
    url: "https://www.ashbyhq.com",
    foundersNote:
      "Ashby is the all-in-one recruiting platform (applicant tracking, scheduling, CRM and analytics) used by fast-growing technology companies. It is remote-first, with an EMEA engineering team working from across the EU and the UK on the product and platform.",
  },
  {
    name: "LiveKit",
    id: "livekit",
    icon: "/livekit.svg",
    url: "https://livekit.io",
    foundersNote:
      "LiveKit builds the open-source real-time infrastructure (WebRTC) behind voice and video AI agents, used by companies such as OpenAI for its voice products. It is a fully remote company, with engineering roles open across EMEA, the UK and Ireland.",
  },
  {
    name: "Granola",
    id: "granola",
    icon: "/granola.png",
    url: "https://www.granola.ai",
    foundersNote:
      "Granola is an AI notepad for meetings that transcribes calls and turns rough notes into structured summaries. The company is based in London, where its small engineering team builds the desktop and mobile apps, the transcription pipeline and the AI features.",
  },
  {
    name: "Docker",
    id: "docker",
    icon: "/docker.svg",
    url: "https://www.docker.com",
    foundersNote:
      "Docker makes the developer tooling behind containers: Docker Desktop, Docker Hub, the Docker Engine and build and supply-chain security tooling. Its engineering organisation is remote across North America and Europe, with UK-based roles on the engine, sandboxes and secure-build teams.",
  },
  {
    name: "incident.io",
    id: "incidentio",
    icon: "/incidentio.png",
    url: "https://incident.io",
    foundersNote:
      "incident.io is a London-based incident management and on-call platform that helps engineering teams declare, run and learn from incidents directly in Slack. Its product, platform and security engineers work from London on a Go and TypeScript stack.",
  },
  {
    name: "Roku",
    id: "roku",
    icon: "/roku.png",
    url: "https://www.roku.com",
    foundersNote:
      "Roku makes the streaming platform behind Roku TV, Roku players and The Roku Channel, with its own operating system, advertising stack and content platform. Its European engineering centres are in Cambridge and Manchester, where teams build the Roku OS, search and recommendations, advertising and cloud services.",
  },
  {
    name: "Box",
    id: "box",
    icon: "/box.png",
    url: "https://www.box.com",
    foundersNote:
      "Box is the enterprise content-management and collaboration platform (cloud storage, workflows, e-signature and AI over documents) used by large organisations worldwide. Its European engineering hub is in Poland, with teams in Warsaw and Wrocław building the core platform, web experiences and machine-learning extraction.",
  },
  {
    name: "xAI",
    id: "xai",
    icon: "/xai.svg",
    url: "https://x.ai",
    foundersNote:
      "xAI is the AI company behind the Grok models and apps, training them on one of the largest GPU clusters in the world. Its London engineering team works on backend APIs, mobile apps, sandboxing infrastructure and X Money.",
  },
  {
    name: "Flo Health",
    id: "flohealth",
    icon: "/flohealth.png",
    url: "https://flo.health",
    foundersNote:
      "Flo Health is the London-based company behind Flo, the women's health and cycle-tracking app used by tens of millions of people every month. Its London engineering team builds the app's AI and machine-learning features, mobile clients, security and infrastructure.",
  },
  {
    name: "Clear Street",
    id: "clearstreet",
    icon: "/clearstreet.png",
    url: "https://clearstreet.io",
    foundersNote:
      "Clear Street is a New York fintech building a cloud-native prime brokerage and clearing platform for institutional investors. Its London engineering team works on front-end trading tools, execution services, payments and regulatory reporting.",
  },
  {
    name: "Apollo Research",
    id: "apolloresearch",
    icon: "/apolloresearch.png",
    url: "https://www.apolloresearch.ai",
    foundersNote:
      "Apollo Research is an AI safety organisation that evaluates frontier models for deceptive and scheming behaviour and advises labs and governments on AI control. It is based in London (with a San Francisco office), where its engineers build evaluation infrastructure, red-teaming tooling and security systems.",
  },
  {
    name: "MoonPay",
    id: "moonpay",
    icon: "/moonpay.png",
    url: "https://www.moonpay.com",
    foundersNote:
      "MoonPay is a crypto payments company that lets people buy, sell and swap digital assets inside wallets, apps and its own products. Its engineering organisation is split between London and New York, with London teams working on blockchain platforms, mobile apps, data and machine learning.",
  },
  {
    name: "CrowdStrike",
    id: "crowdstrike",
    icon: "/crowdstrike.png",
    url: "https://www.crowdstrike.com",
    foundersNote:
      "CrowdStrike is the cybersecurity company behind the Falcon platform, a cloud-native endpoint, cloud and identity protection suite backed by one of the largest threat-intelligence operations in the industry. Its European engineering teams sit in London and Bucharest, with roles across the UK, Ireland, Germany and Romania.",
  },
  {
    name: "Workday",
    id: "workday",
    icon: "/workday.svg",
    url: "https://www.workday.com",
    foundersNote:
      "Workday builds the cloud platform for finance, HR and planning used by most of the Fortune 500. Its European engineering hub is in Dublin, where teams work on distributed systems, the API and agentic AI platform, machine learning and cloud infrastructure.",
  },
  {
    name: "Expedia Group",
    id: "expedia",
    icon: "/expedia.svg",
    url: "https://www.expediagroup.com",
    foundersNote:
      "Expedia Group runs Expedia, Hotels.com and Vrbo, one of the world's largest online travel platforms. Its London engineering centre builds the booking and supply platforms, data platforms and machine-learning systems behind search, pricing and recommendations.",
  },
  {
    name: "ServiceNow",
    id: "servicenow",
    icon: "/servicenow.svg",
    url: "https://www.servicenow.com",
    foundersNote:
      "ServiceNow is the enterprise workflow platform (IT, HR, customer service and AI agents) used by most large companies. Its European engineering presence is in Dublin, where software and site-reliability engineers build and run the Now Platform's cloud infrastructure and AI products.",
  },
];
